import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useLoadTestResource } from './subHooks/useLoadTestResource';
import { useTestNavigator } from './subHooks/useTestNavigator';
import { useAudioActions } from './subHooks/useAudioActions';
import { animationModes } from '../../components/AvatarScene';
import { useAudioRecorder } from '../useAudioRecorder/useAudioRecorder';
import { useGetPartialRecording } from '../useGetParialRecording/useGetPartialRecording';
import {
    PartsMaxDuration,
    intentResponse,
    audioPlayReasons,
    audioEndReasons as promptEndReasons,
    colors,
    loadingThresholdValues,
    audioEndReasons,
    Parts,
    frameNames,
} from '../../constants/constants';
import { formatDate } from '../../service/utils';
import { triggerIntentDetection, useIntentDetection } from './subHooks/useIntentDetection';
import { useTestProgression } from './subHooks/useTestProgression';
import { useInsperaIB } from './subHooks/useInsperaIB';
import { useStatesAndTimers } from './subHooks/useStatesAndTimers';
import { generateWordTimestamps, groupWordsToLines } from '../../service/TranscriptService';
import { isAppOpenedInIframe } from '../../screens/loginScreen/LoginScreen';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { notificationEventEnd, notificationEventStart, uploadAudioFile } from '../../api/authApisV2';
import { useInternetWatcher } from './subHooks/useInternetWatcher';
import { features } from '../../constants/featureFlags';
import { pushFaroEvent } from '../../faroSession';

type promptEndReasonType = keyof typeof promptEndReasons;

export const useTestLogic = () => {
    const navigate = useNavigate();

    const resource = useLoadTestResource();
    const navigator = useTestNavigator({
        promptList: resource.promptList,
        configuration: resource.configuration,
        endTestOnLastPrompt: () => {
            const isPart3 = navigator.currentPrompt?.partName === Parts.PART_3;

            if (resource.ratedContentFetched || !isPart3) {
                recorder.stopFullRecording();
            } else {
                resource.getPart3RatedContent();
            }

            return isPart3 ? resource.ratedContentFetched : true
        },
    });

    const { states, timeouts, intervals, refs, fns, data } = useStatesAndTimers();
    const inspera = useInsperaIB({ setUploadingTest: states.setUploadingTest });

    const appTheme = useSelector((state: RootState) => state.appConfigSlice.appTheme);
    if (inspera.insperaDetails.product === 'HOELT' && appTheme === 'black_on_white') {
        document.documentElement.style.setProperty('--secondary-ochre', colors.hoelt.primaryColor);
        document.documentElement.style.setProperty('--recording-bar-icon-color', colors.ielts.primaryColor);
    }

    const progression = useTestProgression({
        currentPrompt: navigator.currentPrompt,
        nextPrompt: navigator.nextPrompt,
    });
    const audio = useAudioActions({
        currentPrompt: navigator.currentPrompt,
        probeList: resource.probeList,
        currentPartRules: navigator.currentPartRules,
    });

    const recorder = useAudioRecorder({
        audioElementRef: audio.audioRef,
        onSpeechStart: () => {
            timeouts.speakingTimeout.clear();
            timeouts.noResponseTimeout.clear();
            states.setAnimationMode(animationModes.LISTENING);

            triggerIntentDetection(recorder.intentWsRef.current!, true, () => {
                intentDetection.lastIntentPromptRef.current = intentDetection.currentPromptRef.current;
                intentDetection.intentDetectionActiveRef.current = true;
            });
        },
        onSpeechEnd: () => {
            states.setAnimationMode(animationModes.IDLE);

            timeouts.speakingTimeout.start(() => {
                const intentAllowed = !navigator.currentPrompt?.noIntentDetection;
                const isIntentActive =
                    intentDetection.intentDetectionActiveRef.current || !intentDetection.intentUsedRef.current;

                const useIntentDetection = intentAllowed && isIntentActive;

                if (useIntentDetection) {
                    if (hasTimeForIntent) {
                        pushFaroEvent('intent_detection_initial_speech_ended');
                        intentDetection.initialSpeechEndedRef.current = true;
                    } else {
                        afterSpeechHandler();
                    }
                } else {
                    afterSpeechHandler();
                }
            }, fullResponse.seconds * 1000);
        },
        onRecordingEnd: async (blob, timestamps) => {
            const uploadFinalRecordingStartTime = Date.now();
            try {
                states.setUploadingTest(true);
                const responseUpload = await uploadAudioFile(
                    blob,
                    '.wav',
                    `${formatDate(new Date())}_fullrecording`,
                    resource.testTaker,
                    progression.insperaDetails,
                    {
                        recordingStartTimestamp: timestamps?.recordingStartTimestamp ?? undefined,
                        recordingEndTimestamp: timestamps?.recordingEndTimestamp ?? undefined,
                    },
                );

                const uploadFinalRecordingEndTime = Date.now();
                const uploadDuration = (uploadFinalRecordingEndTime - uploadFinalRecordingStartTime) / 1000;
                pushFaroEvent('full_recording_upload_duration', { duration: `${uploadDuration}` });
                if (Number.parseFloat(uploadDuration.toFixed(2)) > loadingThresholdValues.fullFileUploadingTime) {
                    console.error('Final file uploading time exceeded 5 seconds');
                }

                await progression.uploadTestProgression(responseUpload.data.filePath, uploadDuration);

                if (isAppOpenedInIframe()) {
                    notificationEventEnd({
                        testTakerDetails: resource.testTaker,
                        insperaDetails: inspera.insperaDetails,
                        status: 'SUCCESS',
                        endDescription: 'Test ended',
                    });
                }

                if (resource.selectedTaskId) navigate('/preview');
            } catch (error) {
                console.error('Error uploading full recording:', error);
                states.setError('V302');
            } finally {
                inspera.sendMessageToIIB();
                if (!isAppOpenedInIframe()) {
                    states.setUploadingTest(false);
                }
            }
        },
    });

    const partialRecorder = useGetPartialRecording<promptEndReasonType>({
        recordedChunks: recorder.recordedChunks,
        onStart: () => {
            recorder.startVAD();
            runPromptTimers(!navigator.currentPrompt.showTimer && !navigator.currentPrompt.monologueQuestion);
            runNoResponseTimer();
        },

        onEnd: async (blob, data) => {
            const promptEndReason = (data as any)?.promptEndReason;
            const recordingStartTimestamp = (data as any)?.recordingStartTimestamp;
            const recordingEndTimestamp = (data as any)?.recordingEndTimestamp;

            try {
                states.setPromptTimer(0);
                intervals.promptInterval.clear();
                timeouts.encourageTimeout.clear();
                timeouts.noResponseTimeout.clear();
                recorder.stopVAD();
                audio.resetRefs();

                const forceUploadProgression = refs.skipCurrentPartRef.current;
                await progression.logPromptEndData(promptEndReason!, forceUploadProgression); // important to call before uploading audio

                navigator.moveToNextPrompt(refs.skipCurrentPartRef.current, refs.skipCurrentFrameRef.current);

                const filename = `${navigator.currentPromptIdx + 1}_${formatDate(new Date())}`;

                // Disable saving of prompt recording
                const savingRules = process.env.REACT_APP_PROMPT_RECORDING_SAVING_PARTS?.split(',');
                const enabledSaving = String(process.env.REACT_APP_SAVE_PROMPT_RECORDING) === 'true';
                console.log(process.env.REACT_APP_SAVE_PROMPT_RECORDING);
                console.log('PART&PRODUCSTARRAY', savingRules);
                const currentPart = savingRules?.includes(navigator.currentPrompt.partName);
                const product = savingRules?.includes(progression.insperaDetails.product);

                if (!currentPart || !product || !enabledSaving) {
                    console.log('Skipping upload due to saving rules');
                    progression.updateResponseFilePath('');
                    progression.uploadTestProgression();
                    return;
                }

                const responseUpload = await uploadAudioFile(
                    blob,
                    '.wav',
                    filename,
                    resource.testTaker,
                    progression.insperaDetails,
                    {
                        recordingStartTimestamp: recordingStartTimestamp,
                        recordingEndTimestamp: recordingEndTimestamp,
                    },
                );

                pushFaroEvent('partial_recording_uploaded', {
                    promptIdx: String(navigator.currentPromptIdx + 1),
                    size: String(blob.size),
                });

                progression.updateResponseFilePath(responseUpload.data.filePath);
                progression.uploadTestProgression();
            } catch (error) {
                console.error('Error uploading partial recording:', error);
                states.setError('V301');
            }
        },
    });

    const intentDetection = useIntentDetection({
        Websocket: recorder.intentWsRef,
        currentPrompt: navigator.currentPrompt,
        testEnded: navigator.testEnded,
        testTaker: resource.testTaker,
        recordedChunks: recorder.recordedChunks,
        onIntentReceived: (intentData) => {
            if (audio.isPlayingRef.current) {
                pushFaroEvent('intent_result_ignored', { reason: 'audio_playing' });
                return;
            }

            if (
                (!hasTimeForIntent && intentData.intent !== intentResponse.FALLBACK) ||
                refs.skipCurrentPartRef.current ||
                refs.skipCurrentFrameRef.current
            ) {
                questionNavigationHandler(
                    refs.skipCurrentFrameRef.current ? 'MOVE_TO_NEXT_FRAME' : 'MOVE_TO_NEXT_PART',
                );
                return pushFaroEvent('intent_result_ignored', { reason: 'limited_time' });
            }
            if( navigator.currentPrompt.frame === frameNames.part1IntroFrame && intentData.intent === "REPEAT") {
                questionNavigationHandler(
                    refs.skipCurrentFrameRef.current ? 'MOVE_TO_NEXT_FRAME' : 'MOVE_TO_NEXT_PART',
                );
                return ;
            }

            const intentMap = {
                [intentResponse.REPEAT]: {
                    enabled: intentRepeat.enabled,
                    handler: () => repeatHandler('Intent Received as repeat'),
                },
                [intentResponse.MOVE_ON]: {
                    enabled: intentMoveOn.enabled,
                    handler: () => questionNavigationHandler('MOVE_ON_INTENT'),
                },
                [intentResponse.STUCK]: {
                    enabled: intentStuck.enabled,
                    handler: () => playSimplifiedVariantHandler('Intent Received as stuck'),
                },
            };

            const intentAction = intentMap[intentData.intent];

            if (intentAction) {
                if (intentAction.enabled) {
                    progression.updateIntentData(intentData);
                    intentAction.handler();
                } else {
                    console.log('Ignoring intent result due to it being disabled');
                    if (!navigator.currentPrompt.monologueQuestion)
                        questionNavigationHandler('Ignoring intent result due to it being disabled');
                }
            } else {
                //Fallback intent
                progression.updateIntentData(intentData);

                if (!recorder.isSpeakingRef.current) {
                    intentDetection.initialSpeechEndedRef.current
                        ? afterSpeechHandler()
                        : timeouts.speakingTimeout.start(afterSpeechHandler, 2500);
                }
            }
        },
    });

    const error = states.error ?? resource.error ?? progression.error;

    const connection = useInternetWatcher({
        ws: recorder.fullRecordingWSRef,
        start: !!progression.testStartTime && !navigator.testEnded && !error && features.testResiliency,
        onReconnect: () => {
            console.log('Reconnected');

            resumeTest();
            progression.onReconnectLogUpdate();
        },
        onDisconnect: () => {
            console.log('Disconnected');

            pauseTest();
            progression.onDisconnectLogUpdate(audio.isPausedRef.current);
        },
    });

    const hasTimeForIntent =
        navigator.currentPrompt?.subType !== 'INTRO' && !navigator.currentPrompt?.isIntroFrame
            ? data.interrupTimer >= (navigator.currentPartRules.intentThreshold?.seconds ?? 20)
            : true;
    const testCanStart = resource.initialized && recorder.ready && !resource.loading;
    const hideVisualizer = states.animationMode === animationModes.TALKING || !partialRecorder.isPartialActive;
    const { noResponse, fullResponse, partTimeOffset, intentRepeat, intentStuck, intentMoveOn } =
        navigator.currentPartRules;

    const pauseTest = (forceAudioPause?: boolean) => {
        recorder.fullRecordingWSRef.current?.close();

        recorder.stopVAD();
        audio.pause(forceAudioPause);
        fns.pause();
    };

    const resumeTest = () => {
        recorder.createWS();

        !audio.isPausedRef.current && !navigator.currentPrompt.showTimer && recorder.startVAD();
        audio.resume();
        fns.resume();
    };

    // Handler for when test taker finishes speaking
    const afterSpeechHandler = (reason?: promptEndReasonType) => {
        pushFaroEvent('speech_ended', { reason: reason || 'natural_end' });
        if (
            navigator.currentPrompt?.partName === 'PART_3' &&
            refs.isVariantPlayedRef.current === false &&
            audio.encourageRef.current &&
            !refs.skipCurrentPartRef.current
        ) {
            return playBackupVariantHandler(audioPlayReasons.NOT_MUCH_RESPONDED);
        }
        if (audio.encourageRef.current && !refs.skipCurrentPartRef.current) return encourageHandler();

        if (refs.skipCurrentPartRef.current) {
            console.log(audioEndReasons.MOVE_TO_NEXT_PART);
        }
        partialRecorder.stopPartialRecording(reason ?? 'FULL_RESPONSE');
    };

    // General interrupt function used for part timer and prompt max duration timeout
    const interruptHandler = (reason: string) => {
        audio.interrupt({
            onPlay: (audioFileUrl) => {
                pushFaroEvent('interrupt_probe_playing', { reason });
                timeouts.noResponseTimeout.clear();
                timeouts.speakingTimeout.clear();
                recorder.stopVAD(); // this needs to be called immediately
                states.setAnimationMode(animationModes.TALKING);

                progression.logProgressionData(
                    audioFileUrl,
                    audio.isPausedRef.current ? audioPlayReasons.INTERNET_RECONNECT : reason,
                );
            },
            onEnded: () => {
                const endReason =
                    reason === audioPlayReasons.MAX_DURATION_FOR_PART_REACHED ? 'TIME_LIMIT_PART' : 'TIME_LIMIT_PROMPT';
                partialRecorder.stopPartialRecording(endReason);
            },
        });
    };

    const questionNavigationHandler = (reason: string) => {
        audio.questionNavigator({
            onPlay: (audioPath) => {
                console.log('playing move-on probe');
                timeouts.noResponseTimeout.clear();
                timeouts.speakingTimeout.clear();
                recorder.stopVAD(); // this needs to be called immediately
                states.setAnimationMode(animationModes.TALKING);

                progression.logProgressionData(
                    audioPath,
                    audio.isPausedRef.current ? audioPlayReasons.INTERNET_RECONNECT : reason,
                );
            },
            onEnded: () => {
                const endReason =
                    reason === audioPlayReasons.MAX_DURATION_FOR_PART_REACHED ? 'TIME_LIMIT_PART' : 'TIME_LIMIT_PROMPT';
                partialRecorder.stopPartialRecording(endReason);
            },
        });
    };

    // Handler when prompt max duration is reached
    const runPromptTimers = (ignore?: boolean) => {
        if (ignore) return;

        // Max prompt timer
        states.setPromptTimer(navigator.currentPrompt?.maxDuration ?? 0);

        intervals.promptInterval.start(() => {
            states.setPromptTimer((prev) => {
                if (prev <= 0) {
                    intervals.promptInterval.clear();
                    interruptHandler(audioPlayReasons.MAX_DURATION_REACHED);
                    return 0;
                }
                console.log('PROMPT TIMER: ', prev - 1);
                if (refs.timerPausedRef.current) {
                    return prev; // do not decrement if timer is paused
                }
                return prev - 1;
            });
        }, 1000);

        // Min prompt timer
        if (navigator.currentPrompt?.minDuration) {
            timeouts.encourageTimeout.start(() => {
                audio.encourageRef.current = false;
            }, navigator.currentPrompt.minDuration * 1000);
        }
    };

    // Handler for when test taker does not respond
    const runNoResponseTimer = () =>
        timeouts.noResponseTimeout.start(() => {
            pushFaroEvent('no_response_timeout_triggered');
            if (refs.skipCurrentPartRef.current) {
                pushFaroEvent('no_response_ignored', { reason: 'part_time_limited' });
                questionNavigationHandler('MOVE_TO_NEXT_PART');
            } else if (refs.skipCurrentFrameRef.current) {
                questionNavigationHandler('MOVE_TO_NEXT_FRAME');
            } else if( navigator.currentPrompt.frame === frameNames.part1IntroFrame) {
                questionNavigationHandler('MOVE_TO_NEXT_FRAME');
            } else {
                noResponse.playVariant
                    ? playSimplifiedVariantHandler(audioPlayReasons.NO_RESPONSE)
                    : repeatHandler(audioPlayReasons.NO_RESPONSE);
            }
        }, noResponse.seconds * 1000);

    // Handler for repeating the current prompt
    const repeatHandler = (reason: string) => {
        audio.repeat({
            onPlay: (audioFileUrl) => {
                pushFaroEvent('prompt_repeat_started', { reason });
                recorder.stopVAD();
                timeouts.speakingTimeout.clear();
                states.setAnimationMode(animationModes.TALKING);

                progression.logProgressionData(
                    audioFileUrl,
                    audio.isPausedRef.current ? audioPlayReasons.INTERNET_RECONNECT : reason,
                );
            },
            onRepeat: (audioPath, audioType) => {
                states.setAnimationMode(animationModes.TALKING);

                if (audioType === 'ENCOURAGE') {
                    console.log('Repeating encourage probe');
                    recorder.stopVAD();
                    timeouts.speakingTimeout.clear();
                    states.setAnimationMode(animationModes.TALKING);
                }

                progression.logProgressionData(
                    audioPath,
                    audio.isPausedRef.current ? audioPlayReasons.INTERNET_RECONNECT : reason,
                );
            },
            onEnded: () => {
                states.setAnimationMode(animationModes.IDLE);
                recorder.startVAD();
                runNoResponseTimer();
                if (navigator.currentPrompt?.partName === Parts.PART_3 && !navigator.currentPrompt.noIntentDetection) {
                    intentDetection.resetIntentDetection();
                }
            },
            onMaxRepeat: (audioType) => {
                if (!navigator.currentPrompt.monologueQuestion) {
                    if (navigator.currentPrompt?.partName === Parts.PART_3 && audioType === 'PROMPT') {
                        playSimplifiedVariantHandler('TT requested for 2nd repeat');
                    } else {
                        pushFaroEvent('max_repeat_reached', { part: navigator.currentPrompt?.partName });

                        const reasons: Record<typeof audioType, string> = {
                            PROMPT: audioEndReasons.REPEAT_STOP,
                            ENCOURAGE: audioEndReasons.REPEAT_ENCOURAGE,
                            VARIANT_BACKUP: audioEndReasons.REPEAT_VARIANT_BACKUP,
                            VARIANT_SIMPLIFIED: audioEndReasons.REPEAT_VARIANT_SIMPLIFIED,
                        };

                        questionNavigationHandler(reasons[audioType]);
                    }
                }
            },
        });
    };

    // Handler for when test taker does not meet the minimum duration when answering
    const encourageHandler = () => {
        audio.encourage({
            onPlay: (audioFileUrl) => {
                pushFaroEvent('encourage_probe_playing');
                recorder.stopVAD();
                timeouts.speakingTimeout.clear();
                states.setAnimationMode(animationModes.TALKING);

                progression.logProgressionData(
                    audioFileUrl,
                    audio.isPausedRef.current
                        ? audioPlayReasons.INTERNET_RECONNECT
                        : audioPlayReasons.NOT_MUCH_RESPONDED,
                );
            },
            onEnded: () => {
                recorder.startVAD();
                states.setAnimationMode(animationModes.IDLE);
                if (
                    navigator.currentPrompt?.partName === Parts.PART_2 ||
                    (navigator.currentPrompt?.partName === Parts.PART_3 && !navigator.currentPrompt.noIntentDetection)
                ) {
                    intentDetection.resetIntentDetection();
                }
            },
            onMaxEncourage: () => {
                pushFaroEvent('max_encourage_reached', { part: navigator.currentPrompt?.partName });
                partialRecorder.stopPartialRecording('SHORT_RESPONSE');
            },
        });
    };

    // Handler for playing a backup variant of the current prompt
    const playBackupVariantHandler = (reason: string) =>
        audio.playBackupVariant({
            onPlayVariant: (audioFileUrl) => {
                pushFaroEvent('backup_variant_playing', { reason });
                recorder.stopVAD();
                timeouts.speakingTimeout.clear();
                states.setAnimationMode(animationModes.TALKING);
                refs.isVariantPlayedRef.current = true;
                triggerIntentDetection(recorder.intentWsRef.current!, false);

                progression.logProgressionData(
                    audioFileUrl,
                    audio.isPausedRef.current ? audioPlayReasons.INTERNET_RECONNECT : reason,
                );
            },
            onEnded: () => {
                states.setAnimationMode(animationModes.IDLE);
                recorder.startVAD();
                runNoResponseTimer();
                if (navigator.currentPrompt?.partName === Parts.PART_3 && !navigator.currentPrompt.noIntentDetection) {
                    intentDetection.resetIntentDetection();
                }
            },
            onMaxVariantsPlayed: () => {
                //this code might not be reached at all
                pushFaroEvent('max_backup_variants_reached', { part: navigator.currentPrompt?.partName });
                questionNavigationHandler('NO_RESPONSE');
            },
        });

    // Handler for playing a variant of the current prompt
    const playSimplifiedVariantHandler = (reason: string) =>
        audio.playSimplifiedVariant({
            onPlay: (audioPath) => {
                pushFaroEvent('variant_playing', { reason });
                recorder.stopVAD();
                timeouts.speakingTimeout.clear();
                states.setAnimationMode(animationModes.TALKING);
                progression.logProgressionData(audioPath, reason);
                triggerIntentDetection(recorder.intentWsRef.current!, false);

                progression.logProgressionData(
                    audioPath,
                    audio.isPausedRef.current ? audioPlayReasons.INTERNET_RECONNECT : reason,
                );
            },
            onPlayVariant: (audioPath) => {
                refs.isVariantPlayedRef.current = true;
                states.setAnimationMode(animationModes.TALKING);
                triggerIntentDetection(recorder.intentWsRef.current!, false);

                progression.logProgressionData(
                    audioPath,
                    audio.isPausedRef.current ? audioPlayReasons.INTERNET_RECONNECT : reason,
                );
            },
            onEnded: () => {
                states.setAnimationMode(animationModes.IDLE);
                recorder.startVAD();
                runNoResponseTimer();
                if (navigator.currentPrompt?.partName === Parts.PART_3 && !navigator.currentPrompt.noIntentDetection) {
                    intentDetection.resetIntentDetection();
                }
            },
            onMaxVariantsPlayed: () => {
                pushFaroEvent('max_variants_reached', { part: navigator.currentPrompt?.partName });
                questionNavigationHandler('Max simplified variant plays reached, moving to next prompt');
            },
        });

    // Start test and recording
    useEffect(() => {
        if (progression.testStartTime) {
            navigator.startTest();
        } else if (testCanStart && !states.isAnimationLoading) {
            recorder.startFullRecording();
            progression.markTestStartTime();
        }
    }, [testCanStart, states.isAnimationLoading, progression.testStartTime]);

    // Handles the continuity of the test
    useEffect(() => {
        if (!navigator.currentPrompt) return;
        if (navigator.currentPrompt.playAudioTwice) {
            let number = parseInt(navigator.currentPrompt?.audioDuration || '0');
            let wordTimeStamp = generateWordTimestamps(navigator.currentPrompt.markdownText || '', number + 1);
            refs.subTitleRef.current = groupWordsToLines(wordTimeStamp);
            audio.audioRef.current.ontimeupdate = () => {
                if (navigator.currentPrompt.playAudioTwice && !navigator.currentPrompt.audioBarPause) {
                    const currentTime = audio.audioRef.current.currentTime;
                    let transcriptList = refs.subTitleRef.current.filter(
                        (cue: any) => cue.start <= currentTime && cue.end >= currentTime,
                    );
                    let currentPhrase = transcriptList.length > 0 ? transcriptList[0].word : '';
                    states.setCurrentSubTitle(currentPhrase);
                }
            };
        }
        audio.playPrompt({
            onPlay: (audioPath) => {
                pushFaroEvent('prompt_playing', { 
                    promptIdx: String(navigator.currentPromptIdx),
                    part: navigator.currentPrompt.partName,
                });
                refs.isVariantPlayedRef.current = false;
                states.setAnimationMode(animationModes.TALKING);
                triggerIntentDetection(recorder.intentWsRef.current!, false);

                if (audio.isPausedRef.current) {
                    progression.logProgressionData(audioPath, audioPlayReasons.INTERNET_RECONNECT);
                } else {
                    progression.logProgressionData(audioPath, audioPlayReasons.QUESTION_COMPLETED, true);
                    runPromptTimers(!!navigator.currentPrompt.showTimer || !!navigator.currentPrompt.monologueQuestion);
                    refs.timerPausedRef.current = true;
                }
                if (navigator.currentPrompt.isFirstMessage) {
                    if (isAppOpenedInIframe()) {
                        notificationEventStart({
                            testTakerAccessToken: resource.testTaker.testTakerAccessToken,
                            cmsSessionId: resource.testTaker.testTakerSessionId,
                        });
                    }
                }
            },
            onEnded: () => {
                const { maxDuration, showTimer, minDuration, monologueQuestion, isFirstMessage, frame } =
                    navigator.currentPrompt;

                if (showTimer) {
                    states.setPrepTime(maxDuration);
                    states.setAnimationMode(animationModes.IDLE);
                    return;
                }
                refs.currentFrameRef.current = frame;
                if (isFirstMessage) {
                    refs.firstMessageDurationRef.current = Number.parseInt(
                        navigator.currentPrompt?.audioDuration || '3',
                    );
                }
                if (monologueQuestion) states.setMonologueTime(maxDuration);

                if (maxDuration) {
                    if (minDuration) audio.encourageRef.current = true;

                    states.setAnimationMode(animationModes.IDLE);
                    partialRecorder.startPartialRecording();
                } else {
                    states.setAnimationMode(animationModes.IDLE);
                    if (navigator.currentPrompt.playAudioTwice) {
                        navigator.currentPrompt.audioBarPause = true;
                        playPromptWithBeep();
                    } else {
                        progression.uploadTestProgression();
                        navigator.moveToNextPrompt(refs.skipCurrentPartRef.current);
                    }
                }
            },
            onCanPlayThrough: () => {
                refs.timerPausedRef.current = false;
            },
        });
        if (refs.isFirstAudioPlayedRef.current === false) {
            refs.isFirstAudioPlayedRef.current = true;
        }
    }, [navigator.currentPrompt]);

    // Handle preparation time countdown
    useEffect(() => {
        if (!navigator.currentPrompt?.showTimer || states.prepTime < navigator.currentPrompt.maxDuration) return;

        intervals.prepTimeInterval.start(() => {
            states.setPrepTime((prev) => {
                if (prev <= 1) {
                    intervals.prepTimeInterval.clear();
                    navigator.moveToNextPrompt(refs.skipCurrentPartRef.current);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    }, [navigator.currentPrompt, states.prepTime]);

    // Handle Card Text and monologue prompt cleanup
    useEffect(() => {
        if (!navigator.currentPrompt) return;

        const { markdownText } = navigator.currentPrompt;
        const { type } = navigator.currentPrompt;
        if (type === 'rubric') states.setEnableHoeltPart2(true);

        if (markdownText) states.setCardText(markdownText);

        return () => {
            if (navigator.currentPrompt.monologueQuestion) {
                states.setCardText(undefined);
                states.setMonologueTime(0);
                states.setEnableHoeltPart2(false);
            }
            refs.skipCurrentFrameRef.current = false;
        };
    }, [navigator.currentPrompt]);

    const checkForFrameADuration = (prev: any, partDuration: any) => {
        if (refs.currentFrameRef.current === 'Frame_A') {
            let frameADuration = partDuration - prev - refs.firstMessageDurationRef.current;
            if (frameADuration > 100 && frameADuration <= 120 && !refs.skipCurrentFrameRef.current) {
                refs.skipCurrentFrameRef.current = true;
            }
        } else {
            refs.skipCurrentFrameRef.current = false;
        }
    };

    // Handle part timer
    useEffect(() => {
        if (!navigator.currentPrompt) return;

        if (navigator.testEnded) return intervals.partInterval.clear();

        const partDuration = PartsMaxDuration[navigator.currentPrompt.partName as keyof typeof PartsMaxDuration];
        states.setPartTimer(partDuration);

        intervals.partInterval.start(() => {
            states.setPartTimer((prev) => {
                if (prev <= partTimeOffset.seconds) {
                    refs.skipCurrentPartRef.current = true;
                }
                checkForFrameADuration(prev, partDuration);
                if (prev <= 0) {
                    intervals.partInterval.clear();
                    interruptHandler(audioPlayReasons.MAX_DURATION_FOR_PART_REACHED);
                    return 0;
                }

                console.log(`Time left for ${navigator.currentPrompt.partName}: ${prev - 1}`);
                // console.log('pause part time:', refs.timerPausedRef.current);
                if (refs.timerPausedRef.current) {
                    return prev; // do not decrement if timer is paused
                }
                return prev - 1;
            });
        }, 1000);

        return () => {
            intervals.partInterval.clear();
            refs.skipCurrentPartRef.current = false;
            refs.skipCurrentFrameRef.current = false;
        };
    }, [navigator.currentPrompt?.partName, navigator.testEnded]);

    useEffect(() => {
        if (error && testCanStart) {
            pauseTest(true);

            if (isAppOpenedInIframe()) {
                const description = `${error} - Test failed`;

                notificationEventEnd({
                    testTakerDetails: resource.testTaker,
                    insperaDetails: inspera.insperaDetails,
                    status: 'FAILURE',
                    endDescription: description,
                });
            }
        }
    }, [error, testCanStart]);

    const playPromptWithBeep = () => {
        pushFaroEvent('prompt_with_beep_started');
        setTimeout(() => {
            audio.playBeep({
                onPlay: () => {
                    pushFaroEvent('beep_sound_playing');
                },
                onEnded: () => {
                    setTimeout(() => {
                        navigator.currentPrompt.audioBarPause = false;
                        audio.playPrompt({
                            onPlay: (audioPath) => {
                                pushFaroEvent('prompt_playing_after_beep', { 
                                    promptIdx: String(navigator.currentPromptIdx),
                                    part: navigator.currentPrompt.partName,
                                });
                                progression.logProgressionData(audioPath, audioPlayReasons.QUESTION_COMPLETED, true);
                                states.setAnimationMode(animationModes.TALKING);
                            },
                            onEnded: () => {
                                progression.uploadTestProgression();
                                navigator.moveToNextPrompt(refs.skipCurrentPartRef.current);
                            },
                        });
                    }, 1500);
                },
            });
        }, 1500);
    };

    return {
        //status
        loaded: testCanStart,
        testEnded: navigator.testEnded,
        hideVisualizer,
        uploadingTest: isAppOpenedInIframe()
            ? states.uploadingTest && !inspera.awaitingAcknowledgement
            : states.uploadingTest,
        isFailedInsperaTestClosure: inspera.isFailedInsperaTestClosure,
        connection,

        //data
        animationMode: states.animationMode,
        cardText: states.cardText,
        monologueTime: states.monologueTime,
        prepTime: states.prepTime,
        partTimer: states.partTimer,
        promptTimer: states.promptTimer,
        currentSubTitle: states.currentSubTitle,
        insperaDetails: inspera.insperaDetails,
        error,
        currentPrompt: navigator.currentPrompt,
        enableHoeltPart2: states.enableHoeltPart2,

        //funcs,
        setIsAnimationLoading: states.setIsAnimationLoading,

        //refs
        recordedChunks: recorder.recordedChunks,
        appTheme,
        isFirstAudioPlayed: refs.isFirstAudioPlayedRef.current,
    };
};
